import { yupResolver } from "@hookform/resolvers/yup";
import {
	Box,
	Button,
	IconButton,
	Modal,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { CloseWhiteIcon } from "assests/images";
import React, { useEffect, useState, useContext } from "react";
import { useForm } from "react-hook-form";
import { commonModalStyle, createCommonAPICall } from "utility/helpers";
import { sendEmailSchema } from "utility/validations";
import { CustomCopyTooltip } from "components";
import { IRequest, IRequestIdModel } from "utility/interfaces";
import { Constants } from "utility/enums/constants";
import { ISendDefaultMessage } from "utility/interfaces/dashboard/send-busy-notification";
import dashboardService from "services/dashboard-service";
import { SuccessErrorModalDispatchContext } from "contexts/success-error-context";

type ComponentProps = {
	isSendDefaultMessageModalOpen: boolean;
	SendDefaultMessageModalClose: any;
	handleOnClickSend: any;
	row: IRequest;
};

export function SendDefaultMessageModal(props: ComponentProps) {
	// context variables
	const setSuccessErrorContext = useContext(SuccessErrorModalDispatchContext);

	// Usestates
	const [message, setMessage] = useState(
		Constants.ProviderBusyDefaultMessage.toString()
	);
	const [isSent, setIsSent] = useState(false);
	const [isDelivered, setIsDelivered] = useState(false);
	const [deliveredDate, setDeliveredDate] = useState<Date>(new Date());

	// Extract Props
	const {
		isSendDefaultMessageModalOpen,
		SendDefaultMessageModalClose,
		handleOnClickSend,
		row,
	} = props;

	// Yup resolver
	const {
		register,
		handleSubmit,
		reset,
		clearErrors,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(sendEmailSchema),
	});

	const handleSendMessage = () => {
		const request: ISendDefaultMessage = {
			message: message,
			email: row.email,
			phonenumber: row.phone,
		};
		handleOnClickSend(request);
	};

	const getRequestDefaultMessageDetails = async () => {
		const requestBody: IRequestIdModel = {
			requestId: row.queryString,
		};

		const data = await createCommonAPICall({
			requestBody,
			apiService: dashboardService.getRequestDefaultMessageDetails,
			showErrorMessage: true,
			showSuccessMessage: false,
			setSuccessErrorContext,
		});

		if (data && data.data) {
			console.log(data.data);
			setIsSent(data.data.isSent);
			if (data.data.isSent) {
				setMessage(data.data.smsLog.smsTemplate);
				setIsDelivered(data.data.smsLog.isDelivered);
				if (data.data.smsLog.isDelivered) {
					setDeliveredDate(data.data.smsLog.deliveredDate);
				}
			} else {
				setMessage(Constants.ProviderBusyDefaultMessage.toString());
			}
		}
	};

	// useEffects
	useEffect(() => {
		reset();
		clearErrors();
		setMessage(Constants.ProviderBusyDefaultMessage.toString());
		if (isSendDefaultMessageModalOpen) {
			getRequestDefaultMessageDetails();
		}
	}, [isSendDefaultMessageModalOpen]);

	return (
		<>
			<Modal
				open={isSendDefaultMessageModalOpen}
				onClose={SendDefaultMessageModalClose}
				className="send-modal"
			>
				<Box sx={commonModalStyle}>
					<Box className="modal-header">
						<Typography variant="h5">Send Busy Message</Typography>
						<IconButton onClick={SendDefaultMessageModalClose}>
							<img src={CloseWhiteIcon} alt="close" />
						</IconButton>
					</Box>
					<Box className="modal-body">
						<div>
							<Typography variant="h6" className="patient-name">
								<Box className="min-w-125 min-w-sm-110px">
									Patient Name :&nbsp;
								</Box>
								<Box className="name">
									<span>{row.name}</span>
									<CustomCopyTooltip
										canDoCopy={true}
										text="Copy"
										copyText={row.email}
									>
										<span className="email-wrapper">
											&#40;<span>{row.email}</span>&#41;
										</span>
									</CustomCopyTooltip>
									<CustomCopyTooltip
										canDoCopy={true}
										text="Copy"
										copyText={row.phone}
									>
										<span className="email-wrapper">
											&#40;<span>{row.phone}</span>&#41;
										</span>
									</CustomCopyTooltip>
								</Box>
							</Typography>
							{isSent && (
								<>
									<p>Notification already sent.</p>
									<Typography variant="h6" className="patient-name">
										<Box className="min-w-125 min-w-sm-110px">
											Delivery Status:&nbsp;
										</Box>
										<Box className="name">
											<span>{isDelivered ? "Delivered" : "Undelivered"}</span>
											{isDelivered && (
												<span className="email-wrapper">
													&#40;
													<span>{new Date(deliveredDate).toDateString()}</span>
													&#41;
												</span>
											)}
										</Box>
									</Typography>
								</>
							)}
							<TextField
								{...register("message")}
								id="block"
								label="Message"
								value={message}
								onChange={(e) => setMessage(e.target.value)}
								multiline
								maxRows={1}
								inputProps={{ className: "min-h-88px" }}
								fullWidth
								error={errors?.message?.message ? true : false}
								helperText={errors?.message?.message?.toString()}
							/>
						</div>
						<div className="modal-footer">
							{isSent ? (
								<Tooltip title="Resend">
									<Button
										variant="contained"
										onClick={handleSubmit(handleSendMessage)}
									>
										Resend
									</Button>
								</Tooltip>
							) : (
								<Tooltip title="Confirm">
									<Button
										variant="contained"
										onClick={handleSubmit(handleSendMessage)}
									>
										Send
									</Button>
								</Tooltip>
							)}
							<Tooltip title="Cancel">
								<Button
									variant="outlined"
									onClick={SendDefaultMessageModalClose}
								>
									Cancel
								</Button>
							</Tooltip>
						</div>
					</Box>
				</Box>
			</Modal>
		</>
	);
}
